import { Injectable, inject } from '@angular/core';
import type { MergedEducationalArtifactConfig } from '../educational-workflow-config.types';
import {
  WORKFLOW_SLOT_GENERATION_PATH,
  GUIDED_WORKFLOW_SLOTS,
  type WorkflowSlotSnapshot,
} from '../models/conversation-state.model';
import { normalizeSlotText } from '../utils/slot-normalizer.util';
import { isConversationalInterrupt } from '../utils/assistant-conversation-patterns.util';
import { logAssistantDebug } from '../utils/assistant-debug.util';
import { AssistantConversationStateService } from './assistant-conversation-state.service';
import { AssistantWorkflowEngineService } from './assistant-workflow-engine.service';
import { AssistantWorkflowSlotResolverService } from './assistant-workflow-slot-resolver.service';

export const GENERAL_CHAT_HINTS =
  /\b(what is|what are|who is|explain|define|tell me about|how does|why do|phonics|curriculum|research|meaning of)\b/i;

export const TEACHING_TOPIC_HINTS =
  /\b(teach|teaching|lesson|history|overview|ideas|help me understand|who was|classroom)\b/i;

const GENERATION_PATH_QUICK_ALIASES = [
  'quick',
  'generic',
  'simple',
  'fast',
  'defaults',
] as const;

const GENERATION_PATH_CUSTOM_ALIASES = [
  'custom',
  'full',
  'detailed',
  'form',
  'manual',
] as const;

const SUBSTANTIVE_WORD_THRESHOLD = 6;

export type WorkflowContinuationDecision =
  | { action: 'continue' }
  | {
      action: 'enrich';
      patch: Partial<WorkflowSlotSnapshot>;
      pathHint: 'quick' | 'custom';
      topicHint: string;
    }
  | {
      action: 'interrupt';
      reason: 'general_chat' | 'new_artifact';
      artifactHint?: string;
    }
  | { action: 'session_end' };

@Injectable({
  providedIn: 'root',
})
export class AssistantWorkflowContinuationService {
  private readonly conv = inject(AssistantConversationStateService);
  private readonly slotResolver = inject(AssistantWorkflowSlotResolverService);
  private readonly workflowEngine = inject(AssistantWorkflowEngineService);

  evaluate(
    message: string,
    keywordMap: Map<string, string[]>,
    merged: MergedEducationalArtifactConfig | null
  ): WorkflowContinuationDecision {
    const trimmed = message.trim();
    if (!trimmed) {
      return { action: 'continue' };
    }

    if (this.conv.isCancelPhrase(trimmed)) {
      return { action: 'session_end' };
    }

    if (isConversationalInterrupt(trimmed)) {
      const decision: WorkflowContinuationDecision = {
        action: 'interrupt',
        reason: 'general_chat',
      };
      logAssistantDebug('CONTINUATION_DECISION', { message: trimmed, decision });
      return decision;
    }

    const currentSlot = this.conv.currentSlotKey();
    const sessionArtifact = this.conv.sessionArtifact();
    const pathMode = this.conv.generationPathMode();
    const normalized = normalizeSlotText(trimmed);
    const domains = this.workflowEngine.lastDomains();
    const questions = this.workflowEngine.lastQuestions();

    if (currentSlot === WORKFLOW_SLOT_GENERATION_PATH) {
      const pathOnly = this.slotResolver.resolveGenerationPathInput(trimmed);
      if (pathOnly) {
        return { action: 'continue' };
      }

      const enrich = this.parseGenerationPathEnrichment(trimmed);
      if (enrich) {
        return {
          action: 'enrich',
          patch: {},
          pathHint: enrich.path,
          topicHint: enrich.topic,
        };
      }
    } else if (currentSlot) {
      const slotMatch = this.slotResolver.resolveWorkflowSlotInput(
        currentSlot,
        trimmed,
        merged,
        domains,
        questions
      );
      if (slotMatch) {
        return { action: 'continue' };
      }
    }

    if (
      pathMode === 'custom' &&
      currentSlot &&
      (GUIDED_WORKFLOW_SLOTS as readonly string[]).includes(currentSlot)
    ) {
      const wordCount = normalized.split(/\s+/).filter(Boolean).length;
      if (wordCount <= 4 && !GENERAL_CHAT_HINTS.test(trimmed)) {
        return { action: 'continue' };
      }
    }

    const differentArtifact = this.pickDifferentArtifact(
      normalized,
      keywordMap,
      sessionArtifact
    );
    if (differentArtifact) {
      const decision: WorkflowContinuationDecision = {
        action: 'interrupt',
        reason: 'new_artifact',
        artifactHint: differentArtifact,
      };
      logAssistantDebug('CONTINUATION_DECISION', { message: trimmed, decision });
      return decision;
    }

    const wordCount = normalized.split(/\s+/).filter(Boolean).length;
    const substantive = wordCount >= SUBSTANTIVE_WORD_THRESHOLD;

    if (
      GENERAL_CHAT_HINTS.test(trimmed) ||
      TEACHING_TOPIC_HINTS.test(trimmed) ||
      substantive
    ) {
      const decision: WorkflowContinuationDecision = {
        action: 'interrupt',
        reason: 'general_chat',
      };
      logAssistantDebug('CONTINUATION_DECISION', { message: trimmed, decision });
      return decision;
    }

    return { action: 'continue' };
  }

  private parseGenerationPathEnrichment(
    text: string
  ): { path: 'quick' | 'custom'; topic: string } | null {
    const normalized = normalizeSlotText(text);
    const words = normalized.split(/\s+/).filter(w => w.length >= 2);
    const wordSet = new Set(words);

    let path: 'quick' | 'custom' | null = null;
    if (GENERATION_PATH_QUICK_ALIASES.some(a => wordSet.has(a))) {
      path = 'quick';
    }
    if (GENERATION_PATH_CUSTOM_ALIASES.some(a => wordSet.has(a))) {
      path = path ? null : 'custom';
    }
    if (!path) return null;

    const topic = this.extractTopicHint(text);
    if (!topic) return null;
    return { path, topic };
  }

  private extractTopicHint(text: string): string | null {
    const trimmed = text.trim();
    const about = trimmed.match(/\b(?:about|on|for|regarding)\s+(.+)/i);
    if (about?.[1]?.trim()) {
      return about[1].trim();
    }
    const normalized = normalizeSlotText(trimmed);
    const tokens = normalized.split(/\s+/);
    const pathIdx = tokens.findIndex(
      t =>
        (GENERATION_PATH_QUICK_ALIASES as readonly string[]).includes(t) ||
        (GENERATION_PATH_CUSTOM_ALIASES as readonly string[]).includes(t)
    );
    if (pathIdx >= 0 && pathIdx < tokens.length - 1) {
      const rest = tokens.slice(pathIdx + 1).join(' ').trim();
      if (rest.length >= 3) return rest;
    }
    return null;
  }

  private pickDifferentArtifact(
    normalized: string,
    keywordMap: Map<string, string[]>,
    sessionArtifact: string | null
  ): string | null {
    const sessionWords = sessionArtifact
      ? new Set(keywordMap.get(sessionArtifact) ?? [])
      : new Set<string>();
    let best: string | null = null;
    let bestLen = 0;
    for (const [artifact, words] of keywordMap) {
      if (artifact === sessionArtifact) continue;
      for (const w of words) {
        if (sessionWords.has(w)) continue;
        if (w.length >= 3 && (normalized.includes(w) || w.includes(normalized)) && w.length >= bestLen) {
          best = artifact;
          bestLen = w.length;
        }
      }
    }
    return best;
  }
}
